import { DecimalPipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Function } from 'estree';
import { Device } from '@core/utilities/device';

const REFERENCES: { [name: string]: NumberInputComponent } = {};

function hint(value: number, pow: number, surfix: string): string | void {
  if (value >= pow) {
    let base = (value / pow);
    let floor = Math.floor(base);
    return (base > floor ? 'hơn ' : '') + floor + ' ' + surfix;
  }
}

@Component({
  selector: 'number-input',
  templateUrl: './number-input.component.html',
  styleUrls: ['./number-input.component.scss']
})
export class NumberInputComponent implements OnInit, OnDestroy {

  public static of(reference: string): NumberInputComponent {
    return reference && REFERENCES[reference] ? REFERENCES[reference] : undefined;
  }

  private _focus = new EventEmitter<{ target: NumberInputComponent }>();
  private _blur = new EventEmitter<{ target: NumberInputComponent }>();
  private _change = new EventEmitter<{ target: NumberInputComponent }>();

  private _root: HTMLElement;
  private _fit: boolean;
  private _input: HTMLInputElement;
  private _measurer: HTMLElement;
  private _reference: string;

  private _value: number = 0;
  private _onFocusValue: number;

  private _observer = new ResizeObserver(() => {
    this._input.style.width = Math.max(1, this._measurer.getBoundingClientRect().width) + 'px';
  });

  @Input() public decimalPlaces: number = 0;

  @Input() public allowNegativeValue: boolean = false;

  @Input() public hint: boolean = false;

  @Input() public set reference(value: string) {

    if (this._reference != value) {
      if (this._reference && REFERENCES[this._reference]) {
        delete REFERENCES[this._reference];
      }

      this._reference = value;

      if (this._reference) {
        REFERENCES[this._reference] = this;
      }
    }
  }

  public get reference() {
    return this._reference;
  }

  @Input() public set fit(v: boolean) {
    if (this._fit != v) {
      this._fit = v;

      if (this._measurer) {
        if (!this._fit) {
          this._observer.unobserve(this._measurer);
          this._measurer.innerText = '';
        }
        else {
          this._observer.observe(this._measurer);
          this._measurer.innerText = this._input.value;
        }
      }
    }
  }


  @Input() public set value(v: number) {

    this._value = v;

    if (this._input) {
      this._measurer.innerText = this._input.value = isNaN(this._value) ? '' : this.format(this._value.toString(), true);
    }
  }

  public get value() {
    return this._value;
  }

  @Input() public placeHolder: string = '';

  @Input()
  public set disabled(value: boolean) {

    if (this._input) this._input.disabled = value;

    if (value) {
      this._root.setAttribute('disabled', "true");
    }
    else {
      this._root.removeAttribute('disabled');
    }
  }

  public get disabled(): boolean {
    return this._root.hasAttribute('disabled');
  }

  @Input() public condition: (value: number) => boolean;

  @Output()
  public get blur() {
    return this._blur;
  }

  @Output()
  public get change() {
    return this._change;
  }

  @Output()
  public get focus() {
    return this._focus;
  }

  public get focusing() {
    return this._root && this._root.hasAttribute('focus');
  }

  private _text: string = '';
  private _valid: boolean = true;

  public get text() {
    return this._text;
  }

  public get valid() {
    return this._valid;
  }

  public get hintText() {
    if (isNaN(this._value) || (this._input && this._input.value.trim() == '')) return '';

    let v = Math.abs(this._value);
    if (v > 1000000000000000000000 || v == 0) return '';
    return hint(v, 1000000000000000000, 'tỷ tỷ') || hint(v, 1000000000000000, 'triệu tỷ') || hint(v, 1000000000000, 'nghìn tỷ') || hint(v, 1000000000, 'tỷ') || hint(v, 1000000, 'triệu') || hint(v, 1000, 'nghìn') || 'dưới 1 nghìn';
  }

  constructor(private decimal: DecimalPipe, private ref: ElementRef) {
    this._root = this.ref.nativeElement as HTMLElement;
  }

  ngOnInit() {

    this._text = isNaN(this._value) ? '' : this.format(this._value.toString(), true);
    this._valid = !this.condition ? !isNaN(this._value) : this.condition(this._value);
   
    this._root.setAttribute('valid', this._valid ? 'true' : 'false');
    this._input = this._root.querySelector('input');
    this._measurer = this._root.querySelector('[measurer]');

    if (this._root.hasAttribute('disabled') && this._root.getAttribute('disabled') == 'true') {
      this._input.disabled = true;
    }

    if (this._fit) {
      this._input.style.width = "1px";
      this._observer.observe(this._measurer);
      this._measurer.innerText = this._text;
    }
  }

  ngOnDestroy() {
    this._observer.disconnect();
    this._reference = undefined;
  }

  private format(raw: string, blur: boolean): string {

    let values = raw.trim().replace(/([^ 0-9\\.])/g, '').split('.');

    let int = parseInt(values[0]);

    if (isNaN(int)) int = 0;

    int = Math.min(Math.max(int, -1000000000000000000000), 1000000000000000000000);

    if (values.length > 1 && this.decimalPlaces > 0 && (!blur || values[1].length > 0)) {

      let dec = values[1].length <= this.decimalPlaces ? values[1] : values[1].substr(0, this.decimalPlaces);

      return (raw[0] == '-' && this.allowNegativeValue ? '-' : '') + this.decimal.transform(int, (blur ? 1 : Math.min(values[0].length, 21)) + '.0-0', 'en-US') + '.' + dec;
    }
    else {

      return (raw[0] == '-' && this.allowNegativeValue ? '-' : '') + this.decimal.transform(int, (blur ? 1 : Math.min(values[0].length, 21)) + '.0-0', 'en-US');
    }
  }

  public onkeyup(e, blur: boolean = false) {

    this._measurer.innerText = e.target.value;

    if (e.target.value.length > 0 && e.target.value != '-') {

      let ot = e.target.value;
      let nt = this.format(e.target.value, blur);
      let nv = parseFloat(nt.replace(/,/g, ''));

      if (this._value != nv || ot != nt) {

        let sp = e.target.selectionStart;

        this._measurer.innerText = e.target.value = nt;
                
        this._text = nt;
        this._value = nv;

        this._valid = !this.condition || this.condition(this._value);
        this._root.setAttribute('valid', this._valid ? 'true' : 'false');        

        if (sp == 0) {
          e.target.setSelectionRange(0, 0);
        }
        else {

          let dp = ot.indexOf('.');
          let np = sp + nt.length - ot.length + (sp > dp && dp > 0 ? 1 : 0);

          e.target.setSelectionRange(np, np);
        }
      }
    }
    else {      

      if (blur) {
        this._measurer.innerText = e.target.value = '';
      }

      this._text = e.target.value;

      if (!isNaN(this._value)) {

        this._value = NaN;        
        this._valid = !this.condition || this.condition(this._value);
        this._root.setAttribute('valid', this._valid ? 'true' : 'false');        
      }
    }
  }

  public onkeydown(e) {
    this._measurer.innerText = e.target.value;
    if (e.keyCode == 13) e.target.blur();
  }

  public onfocus(e) {
    if (e.target.value == '0' || Device.mobile) {
      let buffer = this._value;
      this.value = NaN;
      this._value = buffer;
    }

    this._root.setAttribute('focus', 'true');
    this._onFocusValue = this._value;

    this._focus.emit({ target: this });      
  }

  public onblur(e) {

    if (e.target.value == '' && this._onFocusValue == this._value) {
      this.value = this._value;
    }

    this.onkeyup(e, true);
    this.blur.emit({ target: this });
    this._root.removeAttribute('focus');

    if ((!isNaN(this._onFocusValue) || !isNaN(this._value)) && this._onFocusValue != this._value) {
      this.change.emit({ target: this });
    }    
  }

  public focusInput() {
    this._input.focus();
  }
}
